import { useEffect, useSyncExternalStore } from "react";
import { clientGateway } from "@/utils/axios";
import { getUserToken } from "@/utils/authUtil";
import { canReadOrgDashboards } from "@/utils/orgDashboardAccess";

/**
 * Whether the HR AI assistant is turned on for the viewer's organisation (TPT-642).
 * ooca admins toggle it per organisation; the gateway answers for the token's own
 * organisation. Anything but an explicit `allowed: true` counts as off, including a
 * failed request, so the menu stays hidden when the api is unreachable or too old.
 */
export type HrAssistantAccess = "loading" | "allowed" | "denied";

const ACCESS_PATH = "/v3/ai/agent/hr_assistant/access";

interface Entry {
  key: string;
  state: HrAssistantAccess;
}

let entry: Entry | null = null;
const listeners = new Set<() => void>();

/**
 * One answer per user and selected organisation. Internal admins switch customer by
 * changing `meta.organization_id`, which changes the key and fetches again. Null when
 * the viewer cannot read org dashboards, who never ask.
 */
const currentKey = (): string | null => {
  const user = getUserToken();
  if (!user || !canReadOrgDashboards(user)) {
    return null;
  }
  return `${user.data.id}:${user.data.meta?.organization_id ?? ""}`;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): HrAssistantAccess => {
  const key = currentKey();
  if (key === null) {
    return "denied";
  }
  return entry?.key === key ? entry.state : "loading";
};

const ensureFetched = (key: string) => {
  if (entry?.key === key) {
    return;
  }
  entry = { key, state: "loading" };
  clientGateway()
    .get<{ data: { allowed: boolean } }>(ACCESS_PATH)
    .then((res) => (res.data.data.allowed === true ? "allowed" : "denied"))
    .catch(() => "denied" as const)
    .then((state) => {
      if (entry?.key === key) {
        entry = { key, state };
        listeners.forEach((listener) => listener());
      }
    });
};

export const useHrAssistantAccess = (): HrAssistantAccess => {
  const key = currentKey();
  useEffect(() => {
    if (key !== null) {
      ensureFetched(key);
    }
  }, [key]);
  return useSyncExternalStore(subscribe, getSnapshot);
};
